import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { defaultHostname, validateHostnameTemplate, type PlatformSettingsDTO, type RehostChangeDTO, type SessionDTO, type WithRerouteJobs } from '@devrobot/shared';
import { BellDot, CircleCheck, Laptop, MonitorSmartphone, Moon, Settings, Sun, SunMoon, TerminalSquare, type LucideIcon } from 'lucide-react';
import { ApiError, api, seg } from '../api/client';
import { qk, useApiMutation, useMeta, useOverview, useSessions, useSettings } from '../api/queries';
import { useUser } from '../api/user';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ErrorAlert } from '../components/ErrorAlert';
import { KeyValue } from '../components/KeyValue';
import { IconChip, PageHeader } from '../components/PageHeader';
import { RehostConfirmDialog } from '../components/RehostConfirmDialog';
import { RelativeTime } from '../components/RelativeTime';
import { SaveBar } from '../components/SaveBar';
import { SkeletonLines, SkeletonTable } from '../components/Skeleton';
import { TabBar, useTab, type TabDef } from '../components/Tabs';
import { NotificationSettings } from './settings/NotificationSettings';
import { useToast } from '../components/Toaster';
import { useClearRocketChatCredentials, useSetRocketChatCredentials } from '../api/notifications';
import { useTheme, type ThemeChoice } from '../hooks/useTheme';
import { THEMES } from '../kit/theme';
import { badgeEnabled, badgePermission, haptic, isStandalone, requestBadgePermission, setBadge, setBadgeEnabled } from '../kit/native';

type Editable = Pick<
  PlatformSettingsDTO,
  | 'allowExternalUsers'
  | 'protectedSecretRevealEnabled'
  | 'sessionTtlHours'
  | 'cliSessionTtlHours'
  | 'roleCacheSeconds'
  | 'backupRetentionCount'
  | 'hostnameTemplate'
  | 'rocketchatEnabled'
  | 'rocketchatUrl'
  | 'rocketchatBotUsername'
>;

const NUMBERS: { key: keyof Editable; label: string; min: number; max: number; unit: string; hint: string }[] = [
  { key: 'sessionTtlHours', label: 'Web session lifetime', min: 1, max: 720, unit: 'hours', hint: 'How long a browser sign-in lasts.' },
  { key: 'cliSessionTtlHours', label: 'CLI session lifetime', min: 1, max: 2160, unit: 'hours', hint: 'How long a CLI sign-in lasts.' },
  { key: 'roleCacheSeconds', label: 'GitLab role cache', min: 0, max: 3600, unit: 'seconds', hint: 'How long a GitLab role is reused before it is checked again. Removing someone in GitLab takes effect within this time.' },
  { key: 'backupRetentionCount', label: 'Backups kept per database', min: 1, max: 365, unit: 'backups', hint: 'Older verified backups are expired; the last usable recovery point is never removed because of a failed backup.' },
];

const FORM_ID = 'platform-settings-form';

/** Kit settings layout: title and description on the left, the controls in a raised card on the right. */
function SplitSection({ title, description, children }: { title: ReactNode; description?: ReactNode; children: ReactNode }) {
  return (
    <section className="grid gap-4 lg:grid-cols-[minmax(0,18rem)_minmax(0,1fr)] lg:gap-10">
      <div>
        <h2 className="font-semibold">{title}</h2>
        {description ? <p className="mt-1 text-sm text-base-content/60">{description}</p> : null}
      </div>
      <div className="card dr-surface min-w-0">
        <div className="card-body gap-5 p-5 sm:p-6">{children}</div>
      </div>
    </section>
  );
}

function ToggleRow({ title, detail, checked, onChange, tone = 'toggle-primary' }: { title: string; detail: string; checked: boolean; onChange: (v: boolean) => void; tone?: string }) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-4 py-3 first:pt-0 last:pb-0">
      <span>
        <span className="block text-sm font-medium">{title}</span>
        <span className="block text-sm text-base-content/60">{detail}</span>
      </span>
      <input type="checkbox" className={`toggle ${tone} shrink-0`} checked={checked} onChange={(e) => onChange(e.target.checked)} />
    </label>
  );
}

function PlatformSettings() {
  const user = useUser();
  const toast = useToast();
  const navigate = useNavigate();
  const q = useSettings();
  const [form, setForm] = useState<Editable | null>(null);
  useEffect(() => {
    if (q.data && !form) setForm(q.data);
  }, [q.data, form]);
  const [pending, setPending] = useState<{ body: Partial<Editable>; changes: RehostChangeDTO[] } | null>(null);
  const save = useApiMutation((body: Partial<Editable> & { confirmMove?: boolean }) => api.patch<WithRerouteJobs<PlatformSettingsDTO>>('/api/v1/settings', body), {
    invalidate: [qk.settings, ['vps'], ['jobs'], ['targets']],
  });

  if (q.isPending || !form)
    return q.isError ? (
      <ErrorAlert error={q.error} onRetry={() => void q.refetch()} />
    ) : (
      <div className="card dr-surface">
        <div className="card-body p-5 sm:p-6">
          <SkeletonLines lines={6} />
        </div>
      </div>
    );
  const s = q.data!;
  const dirty = (Object.keys(form) as (keyof Editable)[]).some((k) => form[k] !== s[k]);
  const templateError = validateHostnameTemplate(form.hostnameTemplate);
  const invalid = Boolean(templateError) || NUMBERS.some((n) => !Number.isFinite(form[n.key] as number) || (form[n.key] as number) < n.min || (form[n.key] as number) > n.max);

  const submit = (body: Partial<Editable>, confirmMove?: boolean) =>
    save.mutate(
      { ...body, ...(confirmMove ? { confirmMove } : {}) },
      {
        onSuccess: (r) => {
          setPending(null);
          setForm(r);
          const jobs = r.rerouteJobs ?? [];
          const moving = jobs.length;
          if (moving) {
            toast.success('Platform settings saved', {
              description: `${moving} application${moving === 1 ? ' is' : 's are'} moving to the new hostname (see Jobs).`,
              action: moving === 1 ? { label: 'View job', onClick: () => navigate(`/jobs/${jobs[0]}`) } : { label: 'View jobs', onClick: () => navigate('/jobs') },
            });
          } else {
            toast.success('Platform settings saved.');
          }
        },
        onError: (err) => {
          // Changing the template moves generated hostnames: the server lists them and waits for confirmation.
          if (err instanceof ApiError && err.confirmation?.field === 'confirmMove') {
            save.reset();
            setPending({ body, changes: err.rehostChanges });
          } else {
            haptic('error');
          }
        },
      },
    );

  const locked = !user.platformAdmin || save.isPending;

  return (
    <>
      <form
        id={FORM_ID}
        onSubmit={(e) => {
          e.preventDefault();
          if (!user.platformAdmin || !dirty || invalid || save.isPending) {
            if (invalid) haptic('error');
            return;
          }
          submit(form);
        }}
        onInvalidCapture={() => haptic('error')}
        className="space-y-8"
      >
        {!user.platformAdmin ? (
          <div role="note" className="alert alert-info alert-soft text-sm">
            Only platform administrators (GitLab group Owners or instance administrators) can change these settings.
          </div>
        ) : null}

        <SplitSection title="GitLab connection" description="Where DevRobot signs people in and reads projects from. Set when the server is installed.">
          <KeyValue
            items={[
              { label: 'GitLab', value: <span className="font-mono">{s.gitlabUrl}</span> },
              { label: 'DevRobot group', value: <span className="font-mono">{s.gitlabGroup}</span> },
              { label: 'GitLab mode', value: s.gitlabMode === 'simulator' ? <span className="badge badge-warning badge-soft badge-sm">simulator</span> : 'GitLab' },
              { label: 'Public URL', value: <span className="font-mono">{s.publicUrl}</span> },
            ]}
          />
        </SplitSection>

        <fieldset disabled={locked} className="min-w-0 space-y-8">
          <SplitSection title="Access" description="Who can sign in, and whether protected secret values can ever be shown.">
            <div className="divide-y divide-hairline">
              <ToggleRow
                title="Allow GitLab external users"
                detail="When off, GitLab accounts marked external cannot sign in even if they are group members."
                checked={form.allowExternalUsers}
                onChange={(v) => setForm({ ...form, allowExternalUsers: v })}
              />
              <ToggleRow
                title="Allow revealing protected-environment secrets"
                detail="When off, nobody can reveal production (protected) secret values, including Owners. Deployments still receive them."
                checked={form.protectedSecretRevealEnabled}
                onChange={(v) => setForm({ ...form, protectedSecretRevealEnabled: v })}
              />
            </div>
          </SplitSection>

          <SplitSection title="Sessions and retention" description="Sign-in lifetimes, how long GitLab roles are trusted, and how many backups are kept.">
            <div className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
              {NUMBERS.map((n) => (
                <fieldset key={n.key} className="fieldset">
                  <legend className="fieldset-legend">{n.label}</legend>
                  <label className="input w-full">
                    <input
                      type="number"
                      className="grow tabular-nums"
                      min={n.min}
                      max={n.max}
                      value={Number.isFinite(form[n.key] as number) ? (form[n.key] as number) : ''}
                      onChange={(e) => setForm({ ...form, [n.key]: e.target.value === '' ? NaN : Number(e.target.value) })}
                    />
                    <span className="text-sm text-base-content/55">{n.unit}</span>
                  </label>
                  <p className="label whitespace-normal">
                    {n.hint} ({n.min}–{n.max})
                  </p>
                </fieldset>
              ))}
            </div>
          </SplitSection>

          <SplitSection title="Generated hostnames" description="The hostname an application gets when it does not choose one by hand.">
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Generated hostname template</legend>
              <input
                className={`input w-full font-mono ${templateError ? 'input-error' : ''}`}
                value={form.hostnameTemplate}
                onChange={(e) => setForm({ ...form, hostnameTemplate: e.target.value })}
                autoCapitalize="off"
                spellCheck={false}
                aria-invalid={templateError ? true : undefined}
                aria-describedby="hostname-template-hint"
              />
              <p id="hostname-template-hint" className="label whitespace-normal">
                {templateError ? (
                  <span className="dr-ink-error">{templateError}</span>
                ) : (
                  <span>
                    One DNS label placed before the domain of each VPS, built from <code>{'{project}'}</code>, <code>{'{environment}'}</code> and <code>{'{service}'}</code>. Example:{' '}
                    <code className="break-anywhere font-mono">{defaultHostname(form.hostnameTemplate.trim(), { project: 'erp', environment: 'development', service: 'app' }, 'eu1.devrobot.dev')}</code>. Changing it moves
                    applications that use their generated hostname.
                  </span>
                )}
              </p>
            </fieldset>
          </SplitSection>

          <SplitSection
            title="Rocket.Chat"
            description="Lets people receive notifications as a direct message. Each person links their own username in their Notifications settings once this is on."
          >
            <ToggleRow
              title="Send notifications to Rocket.Chat"
              detail="Off until a server and a bot credential are set below."
              checked={form.rocketchatEnabled}
              onChange={(v) => setForm({ ...form, rocketchatEnabled: v })}
            />
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Server URL</legend>
              <input
                className="input w-full font-mono"
                type="url"
                value={form.rocketchatUrl}
                onChange={(e) => setForm({ ...form, rocketchatUrl: e.target.value })}
                placeholder="https://chat.example.com"
                autoCapitalize="off"
                spellCheck={false}
              />
              <p className="label whitespace-normal">The base URL of your Rocket.Chat, without a trailing path.</p>
            </fieldset>
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Bot username</legend>
              <input
                className="input w-full font-mono"
                value={form.rocketchatBotUsername}
                onChange={(e) => setForm({ ...form, rocketchatBotUsername: e.target.value })}
                placeholder="devrobot"
                autoCapitalize="off"
                spellCheck={false}
              />
              <p className="label whitespace-normal">Shown as the sender. The account itself is identified by the credential below.</p>
            </fieldset>
          </SplitSection>
        </fieldset>

        <ErrorAlert error={save.error} title="Settings were not saved" />
        {user.platformAdmin && dirty && invalid ? (
          <p role="status" className="text-sm dr-ink-warning">
            Fix the highlighted values before saving.
          </p>
        ) : null}
      </form>
      {user.platformAdmin ? <RocketChatCredentials configured={Boolean(s.rocketchatConfigured)} /> : null}
      {user.platformAdmin
        ? <SaveBar open={dirty} saving={save.isPending} disabled={invalid} onDiscard={() => (setForm(s), save.reset())} form={FORM_ID} label="Save settings" />
        : null}
      {pending ? (
        <RehostConfirmDialog
          title={`Change the hostname template to ${pending.body.hostnameTemplate}?`}
          changes={pending.changes}
          busy={save.isPending}
          error={save.error}
          onCancel={() => (setPending(null), save.reset())}
          onConfirm={() => submit(pending.body, true)}
        />
      ) : null}
    </>
  );
}

/* ── This device (frontend only) ────────────────────────────────────────── */

const THEME_CHOICES: { id: ThemeChoice; label: string; icon: LucideIcon }[] = [
  { id: 'system', label: 'System', icon: SunMoon },
  { id: THEMES.light, label: 'Light', icon: Sun },
  { id: THEMES.dark, label: 'Dark', icon: Moon },
];

function Appearance() {
  const { choice, setChoice } = useTheme();
  return (
    <fieldset className="fieldset gap-3">
      <legend className="fieldset-legend">Appearance</legend>
      <div role="radiogroup" aria-label="Appearance" className="grid grid-cols-3 gap-2 sm:gap-3">
        {THEME_CHOICES.map(({ id, label, icon: Icon }) => (
          <label
            key={id}
            className="flex cursor-pointer flex-col items-center gap-1.5 rounded-field border border-hairline bg-base-100 p-3 text-sm font-medium transition-colors duration-200 has-checked:border-primary/45 has-checked:bg-primary/[0.04] has-checked:shadow-[var(--dr-ring)] has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-primary"
          >
            <input
              type="radio"
              name="appearance"
              className="sr-only"
              value={id}
              checked={choice === id}
              onChange={(e) => {
                const r = (e.currentTarget.parentElement ?? e.currentTarget).getBoundingClientRect();
                setChoice(id, { x: r.left + r.width / 2, y: r.top + r.height / 2 });
              }}
            />
            <Icon className="size-5 opacity-75" aria-hidden="true" />
            {label}
          </label>
        ))}
      </div>
      <p className="label whitespace-normal">System follows the light or dark setting of this phone or computer.</p>
    </fieldset>
  );
}

function InstallApp() {
  const [installed] = useState(isStandalone);
  return (
    <div className="flex items-start gap-3">
      <IconChip icon={MonitorSmartphone} tone={installed ? 'success' : 'primary'} />
      <div className="min-w-0 flex-1 space-y-1">
        <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
          Install the app
          {installed ? (
            <span className="badge badge-soft badge-success badge-sm gap-1">
              <CircleCheck className="size-3.5" aria-hidden="true" /> Installed
            </span>
          ) : null}
        </p>
        {installed ? (
          <p className="text-sm text-base-content/60">You are using DevRobot as an installed app, full screen and with its own icon.</p>
        ) : (
          <p className="text-sm text-base-content/60">
            Open DevRobot full screen with its own icon. On iPhone and iPad, tap <span className="font-medium text-base-content">Share → Add to Home Screen</span> in Safari. On a computer, use the install button in
            the address bar or the browser menu.
          </p>
        )}
      </div>
    </div>
  );
}

// Applies right away on this device; it does not join the platform settings save.
function AppBadge() {
  const toast = useToast();
  const [on, setOn] = useState(badgeEnabled);
  const [permission, setPermission] = useState(badgePermission);
  const attention = useOverview().data?.attention.length;

  const change = async (next: boolean) => {
    if (next && permission !== 'granted') {
      const result = await requestBadgePermission();
      setPermission(result);
      if (result !== 'granted') {
        toast.warning('Badge not allowed', { description: 'Allow notifications for DevRobot in the system settings, then try again.' });
        return;
      }
    }
    setBadgeEnabled(next);
    setOn(next);
    if (next && attention !== undefined) setBadge(attention);
  };

  const unsupported = permission === 'unsupported';
  const denied = permission === 'denied';
  const hint = unsupported
    ? 'Not available here. On iPhone and iPad, install the app first (Share → Add to Home Screen), then turn this on from the installed app.'
    : denied
      ? 'Notifications are blocked for DevRobot. Allow them in the system settings (on iOS: Settings → Notifications → DevRobot), then come back.'
      : `Shows the number of items that need attention${attention !== undefined ? ` (${attention} now)` : ''} on the Home Screen icon. On iOS this needs the installed app and notification permission.`;

  return (
    <label className={`flex items-center justify-between gap-4 ${unsupported || denied ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
      <span className="flex items-start gap-3">
        <IconChip icon={BellDot} tone="secondary" />
        <span className="min-w-0">
          <span className="block text-sm font-medium">App icon badge</span>
          <span id="device-badge-hint" className="block text-sm text-base-content/60">
            {hint}
          </span>
        </span>
      </span>
      <input
        type="checkbox"
        className="toggle toggle-primary shrink-0"
        checked={on && !unsupported && !denied}
        disabled={unsupported || denied}
        aria-describedby="device-badge-hint"
        onChange={(e) => void change(e.target.checked)}
      />
    </label>
  );
}

function ThisDevice() {
  return (
    <SplitSection title="This device" description="Applies only to this phone or computer, right away. Nothing is saved on the server.">
      <Appearance />
      <div className="border-t border-hairline pt-5">
        <InstallApp />
      </div>
      <div className="border-t border-hairline pt-5">
        <AppBadge />
      </div>
    </SplitSection>
  );
}

/* ── Sessions ───────────────────────────────────────────────────────────── */

function Sessions() {
  const q = useSessions();
  const toast = useToast();
  const [revoking, setRevoking] = useState<SessionDTO | null>(null);
  const revoke = useApiMutation((id: string) => api.delete<{ ok: true }>(`/api/v1/auth/sessions/${seg(id)}`), { invalidate: [qk.sessions] });

  if (q.isPending) return <SkeletonTable rows={2} />;
  if (q.isError) return <ErrorAlert error={q.error} onRetry={() => void q.refetch()} />;
  return (
    <>
      <ul className="list -mx-2">
        {q.data.map((s) => {
          const Icon = s.kind === 'web' ? Laptop : TerminalSquare;
          return (
            <li key={s.id} className="list-row items-center rounded-field px-2 hover:bg-base-200/50">
              <span className="grid size-9 place-items-center rounded-field bg-base-200 text-base-content/70">
                <Icon className="size-4" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <p className="flex flex-wrap items-center gap-1.5 text-sm font-medium">
                  <span className="break-anywhere">{s.clientName ?? '—'}</span>
                  <span className="badge badge-ghost badge-sm">{s.kind === 'web' ? 'browser' : 'CLI'}</span>
                  {s.current ? <span className="badge badge-primary badge-soft badge-sm">this session</span> : null}
                </p>
                <p className="text-xs text-base-content/55">
                  Signed in <RelativeTime value={s.createdAt} /> · last used <RelativeTime value={s.lastUsedAt} /> · expires <RelativeTime value={s.expiresAt} />
                </p>
              </div>
              <button type="button" className="btn btn-ghost btn-xs dr-ink-error" onClick={() => setRevoking(s)}>
                {s.current ? 'Sign out' : 'Revoke'}
              </button>
            </li>
          );
        })}
      </ul>
      {revoking ? (
        <ConfirmDialog
          title={revoking.current ? 'Sign out of this browser?' : 'Revoke this session?'}
          confirmLabel={revoking.current ? 'Sign out' : 'Revoke session'}
          tone="error"
          busy={revoke.isPending}
          error={revoke.error}
          onCancel={() => (setRevoking(null), revoke.reset())}
          onConfirm={() =>
            revoke.mutate(revoking.id, {
              onSuccess: () => {
                if (revoking.current) window.location.assign('/login');
                else toast.success('Session revoked', { description: 'That client must sign in again.' });
                setRevoking(null);
              },
              onError: () => haptic('error'),
            })
          }
        >
          <p>
            {revoking.current
              ? 'You will need to sign in with GitLab again.'
              : `The ${revoking.kind === 'cli' ? 'CLI' : 'browser'} session${revoking.clientName ? ` "${revoking.clientName}"` : ''} stops working immediately. Jobs it started keep running.`}
          </p>
        </ConfirmDialog>
      ) : null}
    </>
  );
}

/* ── About ──────────────────────────────────────────────────────────────── */

function About() {
  const q = useMeta();
  if (q.isPending) return <SkeletonLines lines={5} />;
  if (q.isError) return <ErrorAlert error={q.error} />;
  const m = q.data;
  const c = m.compatibility;
  return (
    <>
      <KeyValue
        items={[
          { label: 'DevRobot server', value: m.version },
          { label: 'API version', value: m.apiVersion },
          { label: 'Agent protocol', value: m.agentProtocol },
          { label: 'Manifest schema', value: m.manifestSchemaVersions.join(', ') },
          { label: 'GitLab', value: <span className="font-mono">{m.gitlab.url}</span> },
          { label: 'Group', value: <span className="font-mono">{m.gitlab.group}</span> },
          { label: 'Mode', value: m.gitlab.mode },
        ]}
      />
      <div className="-mx-1 overflow-x-auto border-t border-hairline pt-4">
        <table className="table table-sm">
          <caption className="pb-2 text-left text-sm font-medium">Compatibility matrix</caption>
          <tbody>
            {[
              ['CLI', c.cli],
              ['Server', c.server],
              ['Agent', c.agent],
              ['Manifest schema', c.manifestSchema.join(', ')],
              ['VPS operating systems', c.vpsOs.join('; ')],
              ['Architectures', c.architectures.join(', ')],
              ['Docker', c.docker],
              ['Docker Compose', c.compose],
              ['GitLab', c.gitlab],
            ].map(([k, v]) => (
              <tr key={k} className="border-hairline">
                <th className="font-normal text-base-content/60">{k}</th>
                <td className="break-anywhere">{v}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

const TABS: TabDef[] = [
  { id: 'platform', label: 'Platform' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'device', label: 'This device' },
  { id: 'sessions', label: 'Sessions' },
  { id: 'about', label: 'About' },
];

export default function SettingsPage() {
  const tab = useTab(TABS);
  // Panels stay mounted (only hidden) so unsaved platform edits survive a tab switch.
  const panel = (id: string) => ({ role: 'tabpanel' as const, hidden: tab !== id, className: 'animate-rise space-y-8' });
  return (
    <>
      <PageHeader title="Settings" subtitle="Platform policy, this device, your sign-in sessions and version information." icon={<IconChip icon={Settings} tone="primary" size="lg" />} />
      <div className="space-y-6">
        <TabBar tabs={TABS} active={tab} label="Settings sections" />
        <div {...panel('platform')} aria-label="Platform settings">
          <PlatformSettings />
        </div>
        <div {...panel('notifications')} aria-label="Notification settings">
          <NotificationSettings />
        </div>
        <div {...panel('device')} aria-label="This device">
          <ThisDevice />
        </div>
        <div {...panel('sessions')} aria-label="Your sessions">
          <SplitSection title="Your sessions" description="Browser and CLI sign-ins for your account. Revoke any you do not recognise.">
            <Sessions />
          </SplitSection>
        </div>
        <div {...panel('about')} aria-label="About DevRobot">
          <SplitSection title="About DevRobot" description="Versions of this server and what it is compatible with.">
            <About />
          </SplitSection>
        </div>
      </div>
    </>
  );
}

/**
 * The Rocket.Chat bot credential.
 *
 * Kept out of the main settings form on purpose: it is write-only and goes to its own
 * endpoint, so it must never ride along in the PATCH that returns the whole settings object.
 * The server stores it encrypted and never returns it, so this only ever replaces or clears.
 */
function RocketChatCredentials({ configured }: { configured: boolean }) {
  const set = useSetRocketChatCredentials();
  const clear = useClearRocketChatCredentials();
  const toast = useToast();
  const [authToken, setAuthToken] = useState('');
  const [userId, setUserId] = useState('');

  return (
    <SplitSection
      title="Rocket.Chat bot credential"
      description="A personal access token for the bot account. Stored encrypted and never shown again."
    >
      {configured ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <span className="inline-flex items-center gap-2 text-sm">
            <span className="status status-success status-md" aria-hidden="true" />
            A bot credential is stored.
          </span>
          <button type="button" className="btn btn-sm" disabled={clear.isPending} onClick={() => clear.mutate(undefined, { onSuccess: () => toast.success('Bot credential removed.') })}>
            Remove
          </button>
        </div>
      ) : (
        <div role="note" className="alert alert-info alert-soft text-sm">
          No credential yet. Rocket.Chat notifications stay off until one is set.
        </div>
      )}

      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          set.mutate(
            { authToken: authToken.trim(), userId: userId.trim() },
            {
              onSuccess: () => {
                setAuthToken('');
                setUserId('');
                toast.success('Bot credential saved.');
              },
              onError: () => haptic('error'),
            },
          );
        }}
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <fieldset className="fieldset">
            <legend className="fieldset-legend">User ID</legend>
            <input className="input w-full font-mono" value={userId} onChange={(e) => setUserId(e.target.value)} autoComplete="off" autoCapitalize="off" spellCheck={false} required />
            <p className="label whitespace-normal">The bot account&rsquo;s id (X-User-Id).</p>
          </fieldset>
          <fieldset className="fieldset">
            <legend className="fieldset-legend">Auth token</legend>
            <input className="input w-full font-mono" type="password" value={authToken} onChange={(e) => setAuthToken(e.target.value)} autoComplete="off" required />
            <p className="label whitespace-normal">A personal access token (X-Auth-Token).</p>
          </fieldset>
        </div>
        <ErrorAlert error={set.error} title="The credential was not saved" />
        <div className="flex justify-end">
          <button type="submit" className="btn btn-primary btn-sm" disabled={set.isPending || !authToken.trim() || !userId.trim()}>
            {configured ? 'Replace credential' : 'Save credential'}
          </button>
        </div>
      </form>
    </SplitSection>
  );
}
